import { useState, useEffect, useRef } from "react";
import { api } from "../lib/api";
import { t } from "../i18n/translations";
import type { Language } from "../i18n/translations";

const MVP_CONTACT_URL = import.meta.env.VITE_MVP_CONTACT_URL || "mailto:hello@buzarchitect.ai";

interface UpsellBannerProps {
  projectId: string;
  language: Language;
  /** Pricing info from backend — if provided, renders paywall Banner 2 */
  pricingInfo?: { price: number; is_pilot: boolean } | null;
  /** Name of the showcase dimension from partial assessment */
  showcaseDimension?: string;
  /** Names of skeleton dimensions from partial assessment */
  skeletonDimensions?: string[];
  /** Purchase/unlock handler for $49 assessment */
  onPurchase?: () => Promise<void>;
  /** Purchase/unlock handler for $249 business plan */
  onPurchaseBusinessPlan?: () => Promise<void>;
  /** Hide the $49 assessment card (for second upsell after assessment purchased) */
  hideAssessmentTier?: boolean;
}

interface TierDef {
  nameKey: string;
  priceKey: string;
  taglineKey: string;
  descKey: string;
  ctaKey: string;
  tier: string;
  active: boolean;
}

const TIERS: TierDef[] = [
  {
    nameKey: "upsell.assessment.name",
    priceKey: "upsell.assessment.price",
    taglineKey: "upsell.assessment.tagline",
    descKey: "upsell.assessment.desc",
    ctaKey: "upsell.assessment.cta",
    tier: "full_assessment",
    active: true,
  },
  {
    nameKey: "upsell.bizplan.name",
    priceKey: "upsell.bizplan.price",
    taglineKey: "upsell.bizplan.tagline",
    descKey: "upsell.bizplan.desc",
    ctaKey: "upsell.bizplan.cta",
    tier: "business_plan",
    active: true,
  },
  {
    nameKey: "upsell.mvp.name",
    priceKey: "upsell.mvp.price",
    taglineKey: "upsell.mvp.tagline",
    descKey: "upsell.mvp.desc",
    ctaKey: "upsell.mvp.cta",
    tier: "mvp_build",
    active: true,
  },
];

export default function UpsellBanner({
  projectId,
  language,
  pricingInfo,
  showcaseDimension,
  skeletonDimensions,
  onPurchase,
  onPurchaseBusinessPlan,
  hideAssessmentTier,
}: UpsellBannerProps) {
  const loggedRef = useRef(false);
  const [bpLoading, setBpLoading] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [slowRedirect, setSlowRedirect] = useState(false);

  useEffect(() => {
    if (!loggedRef.current) {
      loggedRef.current = true;
      api.logEvent(projectId, onPurchase ? "paywall_banner_viewed" : "upsell_banner_shown");
    }
  }, [projectId, onPurchase]);

  useEffect(() => {
    if (pricingInfo?.is_pilot) {
      api.logEvent(projectId, "paywall_is_pilot");
    }
  }, [projectId, pricingInfo]);

  // New paywall Banner 2 mode — renders whenever onPurchase is provided.
  if (onPurchase) {
    const isFree = pricingInfo ? (pricingInfo.price === 0 || pricingInfo.is_pilot) : false;
    const priceLoaded = pricingInfo != null;
    const dimensionName = showcaseDimension || "Featured Dimension";
    const formattedPrice = pricingInfo && pricingInfo.price > 0 ? `$${pricingInfo.price / 100}` : null;

    return (
      <div className="bg-slate-800/80 border border-blue-500/20 rounded-2xl p-5 sm:p-6 mt-4">
        <h3 className="text-base font-semibold text-white mb-2">
          Unlock the Full Assessment
        </h3>

        {/* Context: what was covered */}
        <p className="text-sm text-slate-300 mb-4">
          Your interrogation covered <span className="text-white font-medium">{dimensionName}</span> in depth.
          The full assessment investigates all 10 dimensions:
        </p>

        {/* Skeleton dimension list */}
        {skeletonDimensions && skeletonDimensions.length > 0 && (
          <ul className="space-y-1 mb-4">
            {skeletonDimensions.map((dim) => (
              <li key={dim} className="flex items-center gap-2 text-sm text-slate-400">
                <svg className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                {dim}
              </li>
            ))}
          </ul>
        )}

        <p className="text-xs text-slate-500 mb-6">
          Continued interrogation takes approximately 15-20 minutes.
        </p>

        {/* Deliverable comparison list */}
        <div className="space-y-2 mb-6">
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            Verdict and rationale -- included above
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {dimensionName} full analysis -- included above
          </div>
          {[
            "9 remaining dimensions with complete evidence and scoring",
            "Mathematical models (unit economics, sensitivity analysis, break-even)",
            "Competitive intelligence dossier",
            "Risk register with directional recommendations",
            "Decision pattern analysis",
            "Downloadable PDF + shareable executive summary",
          ].map((item) => (
            <div key={item} className="flex items-center gap-2 text-sm text-slate-400">
              <svg className="w-4 h-4 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              {item}
            </div>
          ))}
        </div>

        {/* Pricing tiers */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {/* Full Assessment tier */}
          <div className="bg-slate-900/60 border-2 border-blue-500/40 rounded-xl p-4 flex flex-col">
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-white">
                {t("upsell.assessment.name", language)}
              </h4>
              <p className="text-lg font-bold mt-0.5" data-testid="assessment-price">
                {!priceLoaded ? (
                  <span className="text-slate-400 text-sm">Loading price...</span>
                ) : isFree ? (
                  <>
                    <span className="text-green-400">Free</span>
                    <span className="text-xs text-green-300/60 ml-1">Pilot Access</span>
                  </>
                ) : (
                  <span className="text-blue-400">{formattedPrice}</span>
                )}
              </p>
              <p className="text-xs text-slate-500 italic">
                {t("upsell.assessment.tagline", language)}
              </p>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed flex-1 mb-3">
              {t("upsell.assessment.desc", language)}
            </p>
            <button
              onClick={async () => {
                if (!onPurchase || purchaseLoading) return;
                setPurchaseLoading(true);
                setPurchaseError(null);
                setSlowRedirect(false);
                const timer = setTimeout(() => setSlowRedirect(true), 10000);
                try {
                  await onPurchase();
                } catch (err) {
                  setPurchaseLoading(false);
                  setSlowRedirect(false);
                  setPurchaseError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
                } finally {
                  clearTimeout(timer);
                }
              }}
              disabled={purchaseLoading}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 disabled:cursor-wait text-white text-sm font-medium rounded-lg py-2 transition-colors"
            >
              {purchaseLoading ? (
                <span className="inline-flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {slowRedirect ? "Still loading..." : "Redirecting to checkout..."}
                </span>
              ) : !priceLoaded
                ? "Continue Interrogation"
                : isFree
                ? "Continue Interrogation"
                : `Continue Interrogation -- ${formattedPrice}`}
            </button>
            {purchaseError && (
              <p className="text-xs text-red-400 mt-1.5">{purchaseError}</p>
            )}
          </div>

          {/* Business Plan tier */}
          <div className="bg-slate-900/60 border-2 border-blue-500/40 rounded-xl p-4 flex flex-col">
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-white">{t("upsell.bizplan.name", language)}</h4>
              <p className="text-lg font-bold text-blue-400 mt-0.5">{t("upsell.bizplan.price", language)}</p>
              <p className="text-xs text-slate-500 italic">{t("upsell.bizplan.tagline", language)}</p>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed mb-2">{t("upsell.bizplan.includes", language)}</p>
            <ul className="text-xs text-slate-400 leading-relaxed flex-1 mb-3 space-y-0.5">
              {[
                t("upsell.bizplan.bullet1", language),
                t("upsell.bizplan.bullet2", language),
                t("upsell.bizplan.bullet3", language),
                t("upsell.bizplan.bullet4", language),
                t("upsell.bizplan.bullet5", language),
                t("upsell.bizplan.bullet6", language),
              ].map((b) => (
                <li key={b} className="flex items-start gap-1.5">
                  <span className="mt-0.5">&#x2022;</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={async () => {
                if (onPurchaseBusinessPlan) {
                  if (bpLoading) return;
                  setBpLoading(true);
                  try {
                    await onPurchaseBusinessPlan();
                  } catch {
                    setBpLoading(false);
                  }
                } else {
                  api.logEvent(projectId, "upsell_tier_clicked", { tier: "business_plan" });
                  const subject = encodeURIComponent(`Interested in Business Plan`);
                  const body = encodeURIComponent(`Project: ${projectId}`);
                  window.open(`mailto:filipe@buzarchitect.ai?subject=${subject}&body=${body}`, "_blank");
                }
              }}
              disabled={bpLoading}
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 disabled:cursor-wait text-white text-sm font-medium rounded-lg py-2 transition-colors"
            >
              {bpLoading ? (
                <span className="inline-flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Redirecting to checkout...
                </span>
              ) : t("upsell.bizplan.cta", language)}
            </button>
          </div>

          {/* MVP Build tier — Contact Us */}
          <div className="bg-slate-900/60 border border-slate-600/40 rounded-xl p-4 flex flex-col">
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-white">{t("upsell.mvp.name", language)}</h4>
              <p className="text-lg font-bold text-blue-400 mt-0.5">{t("upsell.mvp.price", language)}</p>
              <p className="text-xs text-slate-500 italic">{t("upsell.mvp.tagline", language)}</p>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed mb-2">{t("upsell.mvp.includes", language)}</p>
            <ul className="text-xs text-slate-400 leading-relaxed flex-1 mb-3 space-y-0.5">
              {[
                t("upsell.mvp.bullet1", language),
                t("upsell.mvp.bullet2", language),
                t("upsell.mvp.bullet3", language),
                t("upsell.mvp.bullet4", language),
              ].map((b) => (
                <li key={b} className="flex items-start gap-1.5">
                  <span className="mt-0.5">&#x2022;</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => {
                api.logEvent(projectId, "upsell_tier_clicked", { tier: "mvp_build" });
                window.open(MVP_CONTACT_URL, "_blank");
              }}
              className="w-full border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 text-sm font-medium rounded-lg py-2 transition-colors"
            >
              {t("upsell.mvp.cta", language)}
            </button>
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          {t("upsell.footer", language)}
        </p>
      </div>
    );
  }

  // Legacy upsell banner (original behavior)
  async function handleTierClick(tier: string, tierName: string) {
    if (tier === "business_plan" && onPurchaseBusinessPlan) {
      if (bpLoading) return;
      setBpLoading(true);
      try {
        await onPurchaseBusinessPlan();
      } catch {
        setBpLoading(false);
      }
      return;
    }
    if (tier === "mvp_build") {
      api.logEvent(projectId, "upsell_tier_clicked", { tier });
      window.open(MVP_CONTACT_URL, "_blank");
      return;
    }
    api.logEvent(projectId, "upsell_tier_clicked", { tier });
    const subject = encodeURIComponent(`Interested in ${tierName}`);
    const body = encodeURIComponent(`Project: ${projectId}`);
    window.open(`mailto:filipe@buzarchitect.ai?subject=${subject}&body=${body}`, "_blank");
  }

  const visibleTiers = hideAssessmentTier
    ? TIERS.filter((t) => t.tier !== "full_assessment")
    : TIERS;

  const gridCols = visibleTiers.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 sm:grid-cols-3";

  return (
    <div className="bg-slate-800/80 border border-blue-500/20 rounded-2xl p-5 sm:p-6 mt-4">
      <h3 className="text-sm font-semibold text-white mb-4">
        {t("upsell.title", language)}
      </h3>

      <div className={`grid ${gridCols} gap-3`}>
        {visibleTiers.map(({ nameKey, priceKey, taglineKey, descKey, ctaKey, tier, active }) => {
          const isMvp = tier === "mvp_build";
          return (
            <div
              key={tier}
              className={
                active
                  ? isMvp
                    ? "bg-slate-900/60 border border-slate-600/40 rounded-xl p-4 flex flex-col"
                    : "bg-slate-900/60 border-2 border-blue-500/40 rounded-xl p-4 flex flex-col"
                  : "bg-slate-900/40 border border-slate-700/30 rounded-xl p-4 flex flex-col opacity-60"
              }
            >
              <div className="mb-3">
                <h4 className={active ? "text-sm font-semibold text-white" : "text-sm font-semibold text-slate-400"}>
                  {t(nameKey, language)}
                </h4>
                <p className={active ? "text-lg font-bold text-blue-400 mt-0.5" : "text-lg font-bold text-slate-500 mt-0.5"}>
                  {t(priceKey, language)}
                </p>
                <p className="text-xs text-slate-500 italic">
                  {t(taglineKey, language)}
                </p>
              </div>
              <p className="text-xs text-slate-400 leading-relaxed flex-1 mb-3">
                {t(descKey, language)}
              </p>
              {active ? (
                isMvp ? (
                  <button
                    onClick={() => handleTierClick(tier, t(nameKey, language))}
                    className="w-full border border-blue-500/40 text-blue-400 hover:bg-blue-500/10 text-sm font-medium rounded-lg py-2 transition-colors"
                  >
                    {t(ctaKey, language)}
                  </button>
                ) : (
                  <button
                    onClick={() => handleTierClick(tier, t(nameKey, language))}
                    disabled={tier === "business_plan" && bpLoading}
                    className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 disabled:cursor-wait text-white text-sm font-medium rounded-lg py-2 transition-colors"
                  >
                    {tier === "business_plan" && bpLoading ? "Processing..." : t(ctaKey, language)}
                  </button>
                )
              ) : (
                <div className="w-full bg-slate-700/40 text-slate-500 text-sm font-medium rounded-lg py-2 text-center cursor-default">
                  {t(ctaKey, language)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
        <svg className="w-3.5 h-3.5 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        {t("upsell.footer", language)}
      </p>
    </div>
  );
}
